import { equal, nested } from '@/api/helpers';
import { Course } from '@/client/courses';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants/routes';
import { useCourseSchedules } from '@/hooks/query';
import { usePlanners } from '@/hooks/query/planner.hooks';
import customizationCompletedImage from '@/images/content/customization-completed.png';
import { AppButton, FlexContainer } from '@/ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const IllustrationImage = styled.div`
  position: relative;
  width: 278px;
  height: 278px;
  flex-shrink: 0;

  img {
    position: absolute;
    inset: 0;
    max-width: none;
    object-fit: cover;
    pointer-events: none;
    width: 100%;
    height: 100%;
  }
`;

const Title = styled.h2`
  font-family: var(--font-family);
  font-weight: 700;
  font-size: var(--heading-medium-font-size);
  line-height: var(--heading-medium-line-height);
  color: var(--black-main);
  margin: 0;
  white-space: pre-wrap;
`;

const Description = styled.div`
  font-family: var(--font-family);
  font-weight: 400;
  font-size: var(--medium-font-size);
  line-height: var(--medium-line-height);
  color: var(--black-main);
  white-space: pre-wrap;

  p {
    margin: 0;
  }
`;

export type CustomizeCourseCompleteStepProps = {
  accountId: string;
  onClose: () => void;
  originalCourse?: Course;
  customizedCourse?: Course;
  onGoToTrainingPlan?: () => void;
};

export const CustomizeCourseCompleteStep: React.FC<
  CustomizeCourseCompleteStepProps
> = ({
  accountId,
  onClose,
  originalCourse,
  customizedCourse,
  onGoToTrainingPlan,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch course schedules to check if original course is scheduled
  const { schedules, isLoading: isLoadingSchedules } = useCourseSchedules(
    {
      take: 1,
      skip: 0,
      enabled: !!accountId && !!originalCourse?.id,
      filters: [
        nested('account', [equal('id', accountId)]),
        equal('executedAt', null),
      ],
    },
    originalCourse?.id,
  );

  const { planners, isLoading: isLoadingPlanners } = usePlanners(
    {
      enabled: !!accountId && !!originalCourse?.id,
    },
    accountId,
  );

  const isOriginalCoursePlanned = planners?.result?.some((plan) =>
    plan.dynamicCourseSchedules.some(
      (plannerCourseSchedule) =>
        plannerCourseSchedule.course.id === originalCourse?.id,
    ),
  );

  const hasScheduledContent =
    (schedules?.count ?? 0) > 0 || isOriginalCoursePlanned;

  const handleActionButtonClick = () => {
    if (hasScheduledContent) {
      navigate(
        RedirectPaths[RedirectPathsEnum.COURSES_REPLACE_SCHEDULED_CONTENT](
          customizedCourse?.id || '',
        ),
      );
    } else {
      onGoToTrainingPlan?.();
    }
  };

  if (isLoadingPlanners || isLoadingSchedules) {
    return null;
  }

  return (
    <FlexContainer direction="column" gap={32} align="center">
      <FlexContainer direction="column" gap={13} align="center">
        <IllustrationImage>
          <img src={customizationCompletedImage} alt="Customization complete" />
        </IllustrationImage>
        <FlexContainer direction="column" gap={10} align="center">
          <Title>{t('course.customization.complete.title')}</Title>
          <Description>
            <p>{t('course.customization.complete.description')}</p>
            <p>{t('course.customization.complete.secondary-description')}</p>
          </Description>
        </FlexContainer>
      </FlexContainer>
      <FlexContainer gap={14} align="center" justify="center">
        <AppButton
          label={t('button.close')}
          onClick={onClose}
          type="outlined"
        />
        <AppButton
          label={
            hasScheduledContent
              ? t('course.customization.complete.replaceScheduledContent')
              : t('course.customization.complete.goToTrainingPlan')
          }
          onClick={handleActionButtonClick}
        />
      </FlexContainer>
    </FlexContainer>
  );
};
